// src/slices/chatSlice.js
import { authHeader } from '../utils/authHeaders';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BASE_QUERYURL, BASE_WORDURL, BASE_DATA_ANALYSIS_URL } from '../components/constants';

// New thunk for fetching chat history
export const fetchChatHistory = createAsyncThunk(
  'chat/fetchChatHistory',
  async ({ historyId, userId, taskId }) => {
    // If taskId is empty string, use null for chat-only histories
    const realTaskId = (taskId === '' || taskId === undefined) ? null : taskId;
    const response = await fetch(`${BASE_QUERYURL}/question-history?history_id=${historyId}&user_id=${userId}&task_id=${realTaskId}`, {
      headers: { ...authHeader() },
    });
    const data = await response.json();
    console.log(data, 'data, res')
    return data.qa_pairs;
  }
);

// FastAPI 422 validation errors return `detail` as an ARRAY of
// {type, loc, msg, input, url} objects, not a string — passing that
// straight to toast.error()/state.error crashes React ("Objects are not
// valid as a React child"), since it ends up rendered directly. The web
// search failure path (routers/question.py) returns `detail` as an OBJECT
// ({message, error_type}) instead, specifically so the frontend can tell
// "timeout" apart from other failures — pull the string out of it too, for
// the same reason: an object landing directly in state.error would crash
// the same way. Any other error shape's `detail`/`message` is normally
// already a plain string.
const normalizeErrorMessage = (detail, fallback) => {
  if (Array.isArray(detail)) {
    return detail.map(e => (e && e.msg) ? e.msg : String(e)).join(' ') || fallback;
  }
  if (detail && typeof detail === 'object') {
    return detail.message || fallback;
  }
  return detail || fallback;
};

// error_type is only ever present on the {message, error_type} object shape
// above — every other error path (network failure, validation error, plain
// string detail) has none, which is exactly the signal used to gate the
// "Retry" action to timeouts specifically (see chatHistory.rejected below).
const extractErrorType = (detail) => (detail && typeof detail === 'object') ? detail.error_type : undefined;

// Word-generation failures (routers/word_generation.py) return `detail` as
// {code} only, no message — the frontend maps known codes to a translated
// toast (see ChatInput.jsx) instead of showing "An error occurred".
const extractErrorCode = (detail) => (detail && typeof detail === 'object') ? detail.code : undefined;

export const sendChatMessage = createAsyncThunk(
  'chat/sendChatMessage',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_QUERYURL}/question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let errorMessage = 'An error occurred';
        let errorType;
        try {
          // Try to parse as JSON first
          const errorData = await response.json();
          errorMessage = normalizeErrorMessage(errorData.detail || errorData.message, errorMessage);
          errorType = extractErrorType(errorData.detail);
        } catch {
          // If JSON parsing fails, try text
          const errorText = await response.text();
          try {
            // Try to parse the text as JSON (in case it's a JSON string)
            const parsed = JSON.parse(errorText);
            errorMessage = normalizeErrorMessage(parsed.detail || parsed.message, errorText);
            errorType = extractErrorType(parsed.detail);
          } catch {
            // If all else fails, use the text as-is
            errorMessage = errorText || errorMessage;
          }
        }
        return rejectWithValue({ message: errorMessage, errorType, payload });
      }
      const data = await response.json();
      return data.response;
    } catch (err) {
      return rejectWithValue({ message: err.message || 'Network error occurred', payload });
    }
  }
);

// Starts a Word-generation background job instead of a normal chat answer —
// POSTs to /word/generate (202 + job_id) rather than /question. The bot
// message this produces carries only { wordJob: { job_id } }; ChatBlob polls
// GET /word/status/{job_id} itself and renders "Creating…" -> download links
// in place, so this thunk (unlike sendChatMessage) never needs to know the
// job's outcome.
export const startWordGeneration = createAsyncThunk(
  'chat/startWordGeneration',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_WORDURL}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let errorMessage = 'An error occurred';
        let errorCode;
        try {
          const errorData = await response.json();
          errorMessage = normalizeErrorMessage(errorData.detail || errorData.message, errorMessage);
          errorCode = extractErrorCode(errorData.detail);
        } catch {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        return rejectWithValue({ message: errorMessage, errorCode, payload });
      }
      const data = await response.json();
      return data; // { job_id }
    } catch (err) {
      return rejectWithValue({ message: err.message || 'Network error occurred', payload });
    }
  }
);

export const saveAnalysisMode = async (historyId, on) => {
  const response = await fetch(`${BASE_DATA_ANALYSIS_URL}/mode/${historyId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ on }),
  });
  if (!response.ok) throw new Error(`analysis mode save failed: ${response.status}`);
};

export const fetchRunningAnalysisJob = async (historyId) => {
  const response = await fetch(`${BASE_DATA_ANALYSIS_URL}/running/${encodeURIComponent(historyId)}`, {
    headers: { ...authHeader() },
  });
  if (!response.ok) throw new Error(`running analysis fetch failed: ${response.status}`);
  return (await response.json()).job || null;
};

const DATA_ANALYSIS_START_TIMEOUT_MS = 45000;

export const startDataAnalysis = createAsyncThunk(
  'chat/startDataAnalysis',
  async (payload, { rejectWithValue }) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), DATA_ANALYSIS_START_TIMEOUT_MS);
    try {
      const response = await fetch(`${BASE_DATA_ANALYSIS_URL}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response.ok) {
        let errorMessage = 'An error occurred';
        let errorCode;
        try {
          const errorData = await response.json();
          errorMessage = normalizeErrorMessage(errorData.detail || errorData.message, errorMessage);
          errorCode = extractErrorCode(errorData.detail);
        } catch {
          errorMessage = 'An error occurred';
        }
        return rejectWithValue({ message: errorMessage, errorCode: errorCode || 'data_analysis_start_failed', payload });
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue({ message: err.message || 'Network error occurred', errorCode: 'data_analysis_start_failed', payload });
    } finally {
      clearTimeout(timer);
    }
  }
);

const findPendingAnalysis = (chatHistory, localId) =>
  chatHistory.find((m) => m.role === 'bot' && m.analysisJob && m.analysisJob.localId === localId);

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    chatHistory: [],
    loading: false,
    // historyId the in-flight request belongs to — `loading` alone is a
    // single app-wide flag, so without this a request started in chat A
    // still reads as "loading" after navigating to chat B, showing chat A's
    // loading indicator on chat B's screen.
    loadingHistoryId: null,
    // Which chat's messages `chatHistory` currently represents. Needed
    // because `chatHistory` itself is one flat, unscoped array — without
    // this, asking a question in chat A, switching to chat B before the
    // answer arrives, then having A's answer land: the reducer would push
    // A's bot message onto whatever `chatHistory` holds AT THAT MOMENT,
    // which by then is chat B's — visually contaminating chat B with an
    // answer that belongs to A. Every push/pop into chatHistory below is
    // gated on this matching the request's own history_id.
    currentHistoryId: null,
    error: null,
    // Set alongside `error` only for known Word-generation failure codes
    // (word_not_enabled, word_generation_already_running) — lets ChatInput.jsx
    // show a specific translated toast instead of the raw generic message.
    errorCode: null,
    // Which chat is currently waiting for an attached image's vision-LLM
    // analysis to finish before its question can even be sent — drives an
    // inline "Analyzing image…" placeholder in the chat itself (same spot
    // the "AI is responding" indicator uses), replacing a floating toast
    // that could end up stacked/duplicated if a send got triggered more
    // than once. Scoped by historyId for the same reason loadingHistoryId
    // is: without it, chat A's indicator could show while chat B is open.
    analyzingImageHistoryId: null,
  },
  reducers: {
    setAnalyzingImage: (state, action) => {
      state.analyzingImageHistoryId = action.payload || null;
    },
    addUserMessage: (state, action) => {

      console.log(action, 'action.payload user')
      state.chatHistory.push({

        role: 'user',
        content: action.payload.content,
        title: action.payload.title || '',
        // Local object-URL thumbnails for images attached to THIS message,
        // shown immediately without waiting for a reload/re-fetch — see
        // ChatBlob.jsx's rendering of message.attachedImages.
        attachedImages: action.payload.attachedImages || [],
        // Display-only — shown immediately, same reasoning as attachedImages
        // above; matches the shape fetchChatHistory.fulfilled restores on
        // reload (qa.restricted_filenames -> restrictedFilenames).
        restrictedFilenames: action.payload.restrictedFilenames || null
      });
    },
    addBotMessage: (state, action) => {
      if (!action.payload || !action.payload.id) {
        console.warn('addBotMessage: Invalid payload', action.payload);
        return;
      }
      state.chatHistory.push({ 
        id: action.payload.id,
        role: 'bot', 
        content: action.payload.content, 
        title: action.payload.title || ''
      });
    },
    clearChatHistory: (state) => {
      state.chatHistory = [];
    },
    clearError: (state) => {
      state.error = null;
      state.errorCode = null;
    },
    // Flips the loading indicator on synchronously, at the moment the user
    // hits send — dispatched from ChatInput.jsx BEFORE the brand-new-chat
    // creation / image-upload awaits, which otherwise delay the real
    // sendChatMessage/startWordGeneration dispatch (and therefore its own
    // `.pending` reducer) by however long those network calls take.
    startSendLoading: (state, action) => {
      state.loading = true;
      state.loadingHistoryId = action.payload;
      state.currentHistoryId = action.payload;
    },
    // Cleanup for an early return during that same pre-send work (e.g. task
    // usage limit reached, chat-history creation failed) — without this the
    // indicator dispatched above would stay stuck on since no thunk ever
    // dispatches to clear it.
    stopSendLoading: (state, action) => {
      if (state.loadingHistoryId === action.payload) {
        state.loading = false;
        state.loadingHistoryId = null;
      }
    },
    // Dispatched right when the user hits send in Word mode, same moment as
    // addUserMessage — so ChatBlob's "Creating document..." card (see its
    // wordJob-without-job_id branch) shows immediately instead of the
    // generic bouncing-dots "AI is responding" indicator, which otherwise
    // stayed on screen for the full /word/generate round trip before the
    // real card appeared. job_id fills in via startWordGeneration.fulfilled
    // below once the backend responds.
    addPendingWordJob: (state) => {
      state.chatHistory.push({
        role: 'bot',
        content: '',
        wordJob: { job_id: null },
      });
    },
    addPendingAnalysisJob: (state, action) => {
      state.chatHistory.push({
        role: 'bot',
        content: '',
        analysisJob: {
          localId: action.payload.localId,
          job_id: null,
          status: 'running',
          files: action.payload.files || [],
          scope: action.payload.scope || 'session',
          payload: action.payload.payload,
        },
      });
    },
    resumeAnalysisJob: (state, action) => {
      const job = action.payload;
      const alreadyShown = state.chatHistory.some((m) => m.analysisJob
        && (m.analysisJob.job_id === job.job_id || (!m.analysisJob.job_id && m.analysisJob.status === 'running')));
      if (alreadyShown) return;
      state.chatHistory.push({
        role: 'bot',
        content: '',
        analysisJob: {
          localId: job.job_id,
          job_id: job.job_id,
          status: 'running',
          files: job.files || [],
          scope: job.scope || 'session',
          step: job.step,
          progress: job.progress,
        },
      });
    },
    completeAnalysisJob: (state, action) => {
      const { jobId, result } = action.payload;
      const message = state.chatHistory.find((m) => m.analysisJob?.job_id === jobId);
      if (!message || !result) return;
      message.id = result.answer_id;
      message.content = result.answer || '';
      message.analysis = result.analysis || null;
      message.analysisJob.status = 'done';
      message.analysisJob.files = result.analysis?.files || message.analysisJob.files;
    },
    retryAnalysisJob: (state, action) => {
      const message = findPendingAnalysis(state.chatHistory, action.payload.localId);
      if (message) {
        message.analysisJob.retryOfJobId = message.analysisJob.job_id || message.analysisJob.retryOfJobId;
        message.analysisJob.job_id = null;
        message.analysisJob.status = 'running';
        message.analysisJob.errorCode = null;
      }
    },
    failAnalysisJob: (state, action) => {
      const message = state.chatHistory.find((m) => m.analysisJob?.job_id === action.payload.jobId);
      if (message) {
        message.analysisJob.status = 'error';
        message.analysisJob.errorCode = action.payload.errorCode || null;
        message.analysisJob.skipped = action.payload.skipped || [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatHistory.pending, (state, action) => {
        state.loading = true;
        state.loadingHistoryId = action.meta.arg.historyId;
        // Navigating to (or loading) a chat is the reliable signal that
        // it's now the one the user is actually viewing.
        state.currentHistoryId = action.meta.arg.historyId;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        // Only clear loading/loadingHistoryId if this settling request is
        // still the one "in flight" — an old request settling after the
        // user already moved to another chat (which set its own
        // loadingHistoryId) must not clear that chat's genuine loading
        // state out from under it, nor hide its loading indicator by
        // resetting the shared `loading` flag to false.
        if (state.loadingHistoryId === action.meta.arg.historyId) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
        if (action.payload && action.payload.length > 0) {
          state.chatHistory = action.payload.map(qa => ({
            role: qa.role,
            content: qa.content,
            title: qa.title || '',
            comment: qa.comment || '',
            source: qa.source || [],
            sources: qa.sources || [],
            web_search: qa.web_search || false,
            hybrid_search: qa.hybrid_search || false,
            id: qa.id,
            // Display-only — which file(s) a "/" picker restriction applied
            // to (never part of `content`/the actual prompt, see ChatInput.jsx).
            restrictedFilenames: qa.restricted_filenames || null,
            generated_document_id: qa.generated_document_id || null,
            generated_document_title: qa.generated_document_title || null,
            generated_document_filename: qa.generated_document_filename || null,
            generated_document_file_size: qa.generated_document_file_size || null,
            analysis: qa.analysis_meta || null
          }));
        } else {
          state.chatHistory = [];
        }
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        if (state.loadingHistoryId === action.meta.arg.historyId) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
        state.error = action.error.message;
      })
      .addCase(sendChatMessage.pending, (state, action) => {
        state.loading = true;
        state.loadingHistoryId = action.meta.arg.history_id;
        // Sending a message only ever happens in the chat currently being
        // viewed — reaffirm it defensively in case this fires without a
        // preceding fetchChatHistory (e.g. a brand-new chat's first message).
        state.currentHistoryId = action.meta.arg.history_id;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        const settledHistoryId = action.meta?.arg?.history_id;
        const stillTracked = state.loadingHistoryId === settledHistoryId;
        // The user may have switched to a DIFFERENT chat before this
        // request resolved — the backend already saved the answer
        // correctly regardless (it doesn't know or care about frontend
        // navigation), so it's not lost, but it must NOT be pushed onto
        // whatever chat is on screen right now. It'll show up correctly
        // next time this chat's history is (re)fetched.
        const stillViewingThisChat = settledHistoryId === state.currentHistoryId;
        if (!action.payload || !action.payload.id) {
          console.warn('sendChatMessage.fulfilled: Invalid payload', action.payload);
          if (stillTracked) {
            state.loading = false;
            state.loadingHistoryId = null;
          }
          return;
        }
        if (stillViewingThisChat) {
          // A retry (see chatHistory.rejected) resends the same question via
          // this same thunk without re-adding a user message — the original
          // one is still sitting there marked `failed`. Clear that now that
          // it actually succeeded, so the Retry action disappears.
          const lastMessage = state.chatHistory[state.chatHistory.length - 1];
          if (lastMessage?.role === 'user' && lastMessage.failed) {
            lastMessage.failed = false;
            lastMessage.errorType = null;
            lastMessage.retryPayload = null;
          }
          state.chatHistory.push({
            id: action.payload.id,
            role: 'bot',
            content: action.payload.content,
            title: action.meta?.arg?.title || '',
            source: action.payload.source || [],
            sources: action.payload.sources || [],
            web_search: action.payload.web_search || false,
            hybrid_search: action.payload.hybrid_search || false,
            // Echoed straight from the request that produced this exact
            // answer (backend never sees/returns it) — a filename -> area
            // label map, since a message can restrict to several files at
            // once. Lets ChatBlob.jsx show, per cited file, the one
            // Knowledge Area it was restricted from, not every area that
            // file happens to be tagged with.
            restrictAreaLabelsByFilename: action.meta?.arg?.restrict_to_folder_labels || null
          });
          state.error = null; // Clear any previous errors on success
        }
        if (stillTracked) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        const settledHistoryId = action.meta?.arg?.history_id;
        if (state.loadingHistoryId === settledHistoryId) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
        // Same cross-chat guard as fulfilled above — if the user has since
        // switched chats, this failure belongs to a chat that's no longer
        // on screen, so its error/optimistic-message cleanup must not touch
        // whatever chat IS currently displayed.
        if (settledHistoryId !== state.currentHistoryId) {
          return;
        }
        // Store the error message so components can display it
        state.error = action.payload?.message || action.error?.message || 'Failed to send message';
        state.errorCode = null;
        // The user message is added right before sendChatMessage, so it should be the last message
        if (state.chatHistory.length > 0) {
          const lastMessage = state.chatHistory[state.chatHistory.length - 1];
          if (lastMessage.role === 'user') {
            if (action.payload?.errorType === 'timeout') {
              // Timeout is transient and Azure-load-dependent — a plain retry
              // usually succeeds (this is literally what "try again" does
              // manually), so keep the question on screen with a Retry action
              // instead of removing it. `retryPayload` is the exact original
              // request body, so retrying resends the identical question.
              lastMessage.failed = true;
              lastMessage.errorType = 'timeout';
              lastMessage.retryPayload = action.payload.payload;
            } else {
              // Every other error class (validation, rate limit, unknown) keeps
              // the pre-existing behavior — remove the optimistic user message
              // rather than offering a blind retry that isn't the right default.
              state.chatHistory.pop();
            }
          }
        }
      })
      .addCase(startDataAnalysis.pending, (state, action) => {
        state.loading = true;
        state.loadingHistoryId = action.meta.arg.history_id;
        state.currentHistoryId = action.meta.arg.history_id;
      })
      .addCase(startDataAnalysis.fulfilled, (state, action) => {
        const settledHistoryId = action.meta?.arg?.history_id;
        if (state.loadingHistoryId === settledHistoryId) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
        if (settledHistoryId !== state.currentHistoryId || !action.payload?.job_id) return;
        const pending = findPendingAnalysis(state.chatHistory, action.meta.arg.local_id);
        const resumed = pending && state.chatHistory.find((m) => m !== pending && m.analysisJob?.job_id === action.payload.job_id);
        if (resumed) {
          resumed.analysisJob.payload = resumed.analysisJob.payload || pending.analysisJob.payload;
          state.chatHistory = state.chatHistory.filter((m) => m !== pending);
        } else if (pending) {
          pending.analysisJob.job_id = action.payload.job_id;
          pending.analysisJob.status = 'running';
          pending.analysisJob.files = action.payload.files || [];
          pending.analysisJob.scope = action.payload.scope || pending.analysisJob.scope;
        }
        state.error = null;
      })
      .addCase(startDataAnalysis.rejected, (state, action) => {
        const settledHistoryId = action.meta?.arg?.history_id;
        if (state.loadingHistoryId === settledHistoryId) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
        if (settledHistoryId !== state.currentHistoryId) return;
        state.error = action.payload?.message || action.error?.message || 'Failed to start data analysis';
        state.errorCode = action.payload?.errorCode || null;
        const localId = action.meta.arg.local_id;
        const index = state.chatHistory.findIndex((m) => m.analysisJob?.localId === localId);
        if (index === -1) return;
        if (action.meta.arg.retry_job_id) {
          state.chatHistory[index].analysisJob.status = 'error';
          return;
        }
        const removeFrom = state.chatHistory[index - 1]?.role === 'user' ? index - 1 : index;
        state.chatHistory.splice(removeFrom, index - removeFrom + 1);
      })
      .addCase(startWordGeneration.pending, (state, action) => {
        state.loading = true;
        state.loadingHistoryId = action.meta.arg.history_id;
        state.currentHistoryId = action.meta.arg.history_id;
      })
      .addCase(startWordGeneration.fulfilled, (state, action) => {
        const settledHistoryId = action.meta?.arg?.history_id;
        const stillTracked = state.loadingHistoryId === settledHistoryId;
        const stillViewingThisChat = settledHistoryId === state.currentHistoryId;
        if (stillViewingThisChat && action.payload?.job_id) {
          // Fill in the placeholder addPendingWordJob already added, rather
          // than pushing a second message — this is what hands WordJobStatus
          // its real jobId and starts polling, with no visible flash since
          // it's the same card the whole time.
          const pending = [...state.chatHistory].reverse().find(
            (m) => m.role === 'bot' && m.wordJob && !m.wordJob.job_id
          );
          if (pending) {
            pending.wordJob.job_id = action.payload.job_id;
          } else {
            state.chatHistory.push({
              role: 'bot',
              content: '',
              wordJob: { job_id: action.payload.job_id },
            });
          }
          state.error = null;
        }
        if (stillTracked) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
      })
      .addCase(startWordGeneration.rejected, (state, action) => {
        const settledHistoryId = action.meta?.arg?.history_id;
        if (state.loadingHistoryId === settledHistoryId) {
          state.loading = false;
          state.loadingHistoryId = null;
        }
        if (settledHistoryId !== state.currentHistoryId) {
          return;
        }
        state.error = action.payload?.message || action.error?.message || 'Failed to start Word generation';
        state.errorCode = action.payload?.errorCode || null;
        // Remove the addPendingWordJob placeholder, then the user message —
        // both were added right before starting the job, same cleanup
        // sendChatMessage's non-timeout error path already does for the
        // user message alone.
        if (state.chatHistory.length > 0) {
          const last = state.chatHistory[state.chatHistory.length - 1];
          if (last.role === 'bot' && last.wordJob && !last.wordJob.job_id) {
            state.chatHistory.pop();
          }
        }
        if (state.chatHistory.length > 0) {
          const lastMessage = state.chatHistory[state.chatHistory.length - 1];
          if (lastMessage.role === 'user') {
            state.chatHistory.pop();
          }
        }
      });
  },
});

export const { addUserMessage, addBotMessage, clearChatHistory, clearError, setAnalyzingImage, startSendLoading, stopSendLoading, addPendingWordJob, addPendingAnalysisJob, resumeAnalysisJob, completeAnalysisJob, retryAnalysisJob, failAnalysisJob } = chatSlice.actions;
export default chatSlice.reducer;